import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sprout, LogOut, Pencil, Trash2, Loader2, ArrowLeft, CalendarDays, Hammer } from "lucide-react";
import AdminRequests from "@/components/AdminRequests";
import AdminNews from "@/components/AdminNews";
import AdminMembers from "@/components/AdminMembers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";
const EMPTY_FORM = { title: "", category: "termin", date: "", time: "", location: "", description: "" };

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
};

const formatDate = (d) => {
  if (!d) return "Datum folgt";
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("kga_admin_token"));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem("kga_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data);
        setChecking(false);
      })
      .catch(() => {
        localStorage.removeItem("kga_admin_token");
        setToken(null);
        setChecking(false);
      });
  }, []);

  const loadEvents = useCallback(async () => {
    const r = await axios.get(`${API}/events`);
    setEvents(r.data);
  }, []);

  useEffect(() => {
    if (user) loadEvents();
  }, [user, loadEvents]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("kga_admin_token", data.token);
      setToken(data.token);
      setUser({ email: data.email, name: data.name });
      toast.success("Angemeldet", { description: `Willkommen, ${data.name}!` });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("kga_admin_token");
    setToken(null);
    setUser(null);
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, form, authHeaders());
        toast.success("Eintrag aktualisiert");
      } else {
        await axios.post(`${API}/events`, form, authHeaders());
        toast.success("Eintrag angelegt", { description: "Der Eintrag ist sofort auf der Website sichtbar." });
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadEvents();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      category: ev.category,
      date: ev.date || "",
      time: ev.time || "",
      location: ev.location || "",
      description: ev.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(`„${ev.title}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, authHeaders());
      toast.success("Eintrag gelöscht");
      await loadEvents();
    } catch (err) {
      toast.error("Löschen fehlgeschlagen", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1A251E]" data-testid="admin-page">
      <header className="border-b border-[#D8D3C5] bg-[#F8F7F2]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="admin-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A382B] text-[#F8F7F2]">
              <Sprout size={18} strokeWidth={1.8} />
            </span>
            <span className="font-display text-xl font-bold text-[#1A382B]">Schönwald · Vorstandsbereich</span>
          </a>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#47544A] hover:text-[#C85A32] transition-colors">
              <ArrowLeft size={15} /> Zur Website
            </a>
            {user && (
              <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-[#D8D3C5] px-4 py-2 text-sm font-semibold text-[#1A382B] hover:border-[#B93829] hover:text-[#B93829] transition-colors">
                <LogOut size={14} /> Abmelden
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-12">
        {checking ? (
          <p className="text-center text-[#6F7E73]" data-testid="admin-loading">Sitzung wird geprüft …</p>
        ) : !user ? (
          <div className="mx-auto max-w-md">
            <form onSubmit={login} className="rounded-2xl border border-[#D8D3C5] bg-white p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="admin-login-form">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A382B]">Anmeldung für den Vorstand</h1>
              <p className="mt-2 mb-6 text-sm text-[#47544A]">
                Hier pflegen Sie Termine und Arbeitseinsätze – ganz ohne Programmierung.
              </p>
              {error && (
                <p className="mb-4 rounded-xl bg-[#B93829]/10 px-4 py-3 text-sm font-medium text-[#B93829]" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <label className={LABEL}>E-Mail</label>
              <input type="email" required className={FIELD} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vorstand@kga-schoenwald.de" data-testid="admin-email-input" />
              <div className="mt-4">
                <label className={LABEL}>Passwort</label>
                <input type="password" required className={FIELD} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
              </div>
              <button type="submit" disabled={loading} data-testid="admin-login-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] disabled:opacity-60">
                {loading && <Loader2 size={16} className="animate-spin" />}
                Anmelden
              </button>
            </form>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="admin-dashboard">
            <div className="lg:col-span-2">
              <form onSubmit={saveEvent} className="rounded-2xl border border-[#D8D3C5] bg-white p-6 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="admin-event-form">
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B]">
                  {editingId ? "Eintrag bearbeiten" : "Neuer Eintrag"}
                </h2>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className={LABEL}>Art *</label>
                    <select className={FIELD} value={form.category} onChange={set("category")} data-testid="event-category-select">
                      <option value="termin">Termin</option>
                      <option value="arbeitseinsatz">Arbeitseinsatz</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Titel *</label>
                    <input required className={FIELD} value={form.title} onChange={set("title")} placeholder="z. B. Sommerfest" data-testid="event-title-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={LABEL}>Datum</label>
                      <input type="date" className={FIELD} value={form.date} onChange={set("date")} data-testid="event-date-input" />
                    </div>
                    <div>
                      <label className={LABEL}>Uhrzeit</label>
                      <input type="time" className={FIELD} value={form.time} onChange={set("time")} data-testid="event-time-input" />
                    </div>
                  </div>
                  <p className="text-xs text-[#6F7E73]">Ohne Datum erscheint „Datum folgt“ auf der Website.</p>
                  <div>
                    <label className={LABEL}>Ort</label>
                    <input className={FIELD} value={form.location} onChange={set("location")} placeholder="z. B. Vereinsheim" data-testid="event-location-input" />
                  </div>
                  <div>
                    <label className={LABEL}>Beschreibung</label>
                    <textarea rows={4} className={FIELD} value={form.description} onChange={set("description")} data-testid="event-description-input" />
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button type="submit" disabled={saving} data-testid="event-submit-button" className="inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-6 py-3 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] disabled:opacity-60">
                    {saving && <Loader2 size={15} className="animate-spin" />}
                    {editingId ? "Speichern" : "Anlegen"}
                  </button>
                  {editingId && (
                    <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }} data-testid="event-cancel-edit-button" className="text-sm font-medium text-[#6F7E73] hover:text-[#B93829]">
                      Abbrechen
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="lg:col-span-3">
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B] mb-5">Aktuelle Einträge</h2>
              {events.length === 0 && (
                <p className="rounded-2xl border border-[#D8D3C5] bg-white p-6 text-sm text-[#47544A]" data-testid="admin-events-empty">
                  Noch keine Einträge vorhanden.
                </p>
              )}
              <div className="space-y-3" data-testid="admin-events-list">
                {events.map((ev, i) => (
                  <div key={ev.id} className="flex items-start gap-4 rounded-2xl border border-[#D8D3C5] bg-white p-5" data-testid={`admin-event-${i}`}>
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${ev.category === "termin" ? "bg-[#E2ECE5] text-[#1A382B]" : "bg-[#D4A359]/20 text-[#8a6420]"}`}>
                      {ev.category === "termin" ? <CalendarDays size={17} /> : <Hammer size={17} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-jbm text-xs tracking-wider uppercase text-[#5A826B]">
                        {ev.category === "termin" ? "Termin" : "Arbeitseinsatz"} · {formatDate(ev.date)}
                        {ev.time ? ` · ${ev.time} Uhr` : ""}
                      </p>
                      <p className="font-semibold text-[#1A382B]">{ev.title}</p>
                      {ev.description && <p className="mt-1 text-sm text-[#47544A] line-clamp-2">{ev.description}</p>}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button onClick={() => startEdit(ev)} data-testid={`event-edit-${i}`} aria-label="Bearbeiten" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D3C5] text-[#1A382B] hover:border-[#5A826B] transition-colors">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => removeEvent(ev)} data-testid={`event-delete-${i}`} aria-label="Löschen" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D3C5] text-[#B93829] hover:border-[#B93829] transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <AdminNews token={token} />
          <AdminMembers token={token} />
          <AdminRequests token={token} />
          </>
        )}
      </main>
    </div>
  );
}
